import { CycleTimeModel } from '@/libs/Api';
import { parseCycleTimeToMinutes } from '@/libs/tpm-analytics/core/cycle-time.utils';
import { locationsStore } from '@/libs/locations/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { CycleTimesRepository } from '@/libs/tpm-analytics/data/cycle-times.repository';

type NullableString = string | null | undefined;

export interface CycleTimeResolutionArgs {
    workplaceId?: NullableString;
    workplaceCode?: NullableString;
    workplaceName?: NullableString;
    productId?: NullableString;
    productCode?: NullableString;
    productName?: NullableString;
    productCollectionId?: NullableString;
    productCollectionName?: NullableString;
    /**
     * Location chain ordered from nearest (workplace location) to root ancestors.
     * If not provided, resolver will attempt to resolve by workplace-only strategies.
     */
    locationChain?: string[];
    locationChainCodes?: string[];
    locationChainNames?: string[];
}

interface ResolverMaps {
    // Mapy podle ID
    byProductWorkplace: Map<string, number>;
    byProductCollectionWorkplace: Map<string, number>;
    byProductLocation: Map<string, number>;
    byProductCollectionLocation: Map<string, number>;
    byWorkplace: Map<string, number>;
    // Univerzální fallback mapa - ukládá všechny kombinace (ID|CODE|NAME)
    // Klíč: "productIdentifier|workplaceIdentifier" kde identifier může být ID, CODE nebo NAME
    fallbackMap: Map<string, number>;
}

function key2(a?: NullableString, b?: NullableString): string {
    return `${a ?? ''}|${b ?? ''}`;
}

function key2Strict(a: string, b: string): string {
    return `${a}|${b}`;
}

/**
 * Build ancestry chains for locations used by the provided shifts' workplaces.
 * Returns map workplaceId -> ordered array of location ids (nearest first).
 */
export function buildLocationChainsForShifts(shifts: any[]): Map<string | null, string[]> {
    const locs = (locationsStore.useGetter(CrudGetter.Data) as any[]) || [];
    const byId = new Map<string, any>(locs.map((l: any) => [l.id, l]));
    const chains = new Map<string | null, string[]>();
    for (const s of shifts) {
        const wid: string | null = (s as any)?.workplaceId ?? (s as any)?.workplace?.id ?? null;
        if (chains.has(wid)) continue;
        let lid: string | null | undefined = (s as any)?.workplace?.location?.id ?? (s as any)?.workplace?.locationId ?? null;
        const guard = new Set<string>();
        const chain: string[] = [];
        while (lid && !guard.has(lid)) {
            guard.add(lid);
            chain.push(lid);
            const loc = byId.get(lid);
            lid = (loc?.parentalLocationId as NullableString) ?? null;
        }
        chains.set(wid, chain);
    }
    return chains;
}

// removed: mapCycleTimesToMinutes – stará utilita pro mapování jednotlivých typů

/**
 * cycle-time.resolver.ts - Vysokovýkonný resolver pro cyklové časy
 *
 * ÚČEL:
 * Pro výpočet OEE Performance potřebujeme znát cyklový čas (cycle time) pro každou
 * kombinaci produkt + pracoviště v každé směně. Naive přístup (dotaz na DB pro každou
 * kombinaci) by byl extrémně pomalý.
 *
 * ŘEŠENÍ:
 * 1. Jednorázově načteme VŠE CHNY relevantní cyklové časy z DB (jeden GraphQL dotaz)
 * 2. Sestavíme mapy pro rychlé vyhledávání (O(1) lookup)
 * 3. Vrátíme resolver funkci, která pro každou kombinaci najde správný cyklový čas
 *
 * PRIORITA PRAVIDEL (od nejvyšší):
 * 1. ByProductAndWorkplace - specifické pravidlo pro produkt + pracoviště
 * 2. ByProductCollectionAndWorkplace - pravidlo pro kolekci produktů + pracoviště
 * 3. ByProductAndLocation - produkt + lokace (postupně po předcích)
 * 4. ByProductCollectionAndLocation - kolekce + lokace (postupně po předcích)
 * 5. ByWorkplace - obecné pravidlo jen pro pracoviště
 *
 * Příklad: Hledáme CT pro "Výrobek A" na "Lince 1" v "Hale 2"
 *   1. Existuje přesné pravidlo "Výrobek A + Linka 1"? → Použij
 *   2. Jinak: Existuje "Kolekce výrobků XY + Linka 1"? → Použij
 *   3. Jinak: Existuje "Výrobek A + Hala 2"? → Použij
 *   4. Jinak: Existuje "Kolekce XY + Hala 2"? → Použij
 *   5. Jinak: Existuje obecné pro "Linka 1"? → Použij
 *   6. Jinak: null (CT není definován)
 *
 * Builds a high-performance resolver for cycle times based on new CycleTime entity, with priority order:
 * 1) ByProductAndWorkplace, 2) ByProductCollectionAndWorkplace, 3) ByProductAndLocation,
 * 4) ByProductCollectionAndLocation, 5) ByWorkplace.
 * Performs batched API calls and caches results in maps for O(1) resolution.
 */
export interface CycleTimeResolutionResult {
    minutes: number | null;
    /**
     * 1..5 according to priority, or null when not found at all
     * 1: product+workplace, 2: productCollection+workplace, 3: product+location, 4: productCollection+location, 5: workplace
     */
    matchType: 1 | 2 | 3 | 4 | 5 | null;
}

/**
 * Sestaví resolver funkci pro vyhledávání cyklových časů.
 *
 * PROCES:
 * 1. ANALÝZA SMĚN: Projdeme všechny směny a sesbíráme unikátní ID:
 *    - workplaceIds (pracoviště)
 *    - productIds (produkty ve směnách)
 *    - productCollectionIds (kolekce produktů)
 *    - locationIds (lokace pracovišť + všichni předci v hierarchii)
 *
 * 2. NAČTENÍ DAT: Jeden GraphQL dotaz pro všechny cyklové časy
 *
 * 3. SESTAVENÍ MAP: Vytvoříme 5 map pro rychlé vyhledávání podle typu pravidla
 *
 * 4. VRÁCENÍ RESOLVERU: Funkce, která pro danou kombinaci najde správný CT
 *
 * Výhoda: Místo tisíců DB dotazů použijeme 1 dotaz + tisíce O(1) lookupů v paměti.
 *
 * @param shifts Pole směn (používáme pro zjištění, jaké kombinace budeme potřebovat)
 * @returns Resolver funkce (args → {minutes, matchType})
 */
export async function buildCycleTimeResolver(
    shifts: any[]
): Promise<(args: CycleTimeResolutionArgs) => CycleTimeResolutionResult> {
    // FÁZE 1: Sesbíráme všechny unikátní ID ze směn
    const workplaceIds = new Set<string>();
    const locationIds = new Set<string>();
    const productIds = new Set<string>();
    const productCollectionIds = new Set<string>();

    // Projdeme všechny směny a jejich produkce
    for (const s of shifts) {
        // ID pracoviště
        const wid: NullableString = (s as any)?.workplaceId ?? (s as any)?.workplace?.id ?? null;
        if (wid) workplaceIds.add(wid);

        // Produkty a jejich kolekce ze shiftProductions
        const prods: any[] = Array.isArray((s as any)?.shiftProductions) ? ((s as any).shiftProductions as any[]) : [];
        for (const p of prods) {
            const pid: NullableString = (p as any)?.productId ?? (p as any)?.product?.id ?? null;
            const pcid: NullableString = (p as any)?.product?.productCollectionId ?? null;
            if (pid) productIds.add(pid);
            if (pcid) productCollectionIds.add(pcid);
        }

        // Lokace pracoviště
        const lid: NullableString = (s as any)?.workplace?.location?.id ?? (s as any)?.workplace?.locationId ?? null;
        if (lid) locationIds.add(lid);
    }

    // Sestavíme hierarchii lokací (pro pravidla typu "Product + Location")
    // Potřebujeme i všechny předky, protože pravidlo může být definováno na vyšší úrovni
    const locChains = buildLocationChainsForShifts(shifts);
    for (const chain of locChains.values()) {
        for (const lid of chain) locationIds.add(lid);
    }

    // FÁZE 2: Načteme všechny relevantní cyklové časy jedním dotazem
    const all: CycleTimeModel[] = await CycleTimesRepository.fetchAllCycleTimes();

    // Build maps with filtering to relevant ids to keep memory small
    const maps: ResolverMaps = {
        // ID mapy (priorita 1)
        byProductWorkplace: new Map<string, number>(),
        byProductCollectionWorkplace: new Map<string, number>(),
        byProductLocation: new Map<string, number>(),
        byProductCollectionLocation: new Map<string, number>(),
        byWorkplace: new Map<string, number>(),
        // Univerzální fallback mapa (priorita 2) - obsahuje všechny kombinace identifikátorů
        fallbackMap: new Map<string, number>(),
    };

    function typeKey(v: any): string {
        return String(v || '').toUpperCase();
    }

    // Helper funkce pro přidání všech kombinací identifikátorů do fallback mapy
    function addToFallbackMap(identifiers1: string[], identifiers2: string[], minutes: number) {
        for (const id1 of identifiers1) {
            for (const id2 of identifiers2) {
                if (id1 && id2) {
                    const key = key2Strict(id1, id2);
                    if (!maps.fallbackMap.has(key)) {
                        maps.fallbackMap.set(key, minutes);
                    }
                }
            }
        }
    }

    for (const ct of all) {
        const minutes = parseCycleTimeToMinutes(ct.timePerUnit || null);
        if (!(minutes && minutes > 0)) continue;
        
        switch (typeKey(ct.cycleTimeType)) {
            case 'BY_PRODUCT_AND_WORKPLACE': {
                const pid = ct.productId as any;
                const wid = ct.workplaceId as any;
                const pcode = (ct as any).product?.code;
                const pname = (ct as any).product?.name;
                const wcode = (ct as any).workplace?.code;
                const wname = (ct as any).workplace?.name;
                
                // Prioritní mapa podle ID
                if (pid && wid) {
                    const key = key2Strict(pid, wid);
                    if (!maps.byProductWorkplace.has(key)) {
                        maps.byProductWorkplace.set(key, minutes);
                    }
                }
                
                // Fallback: všechny kombinace ID/CODE/NAME pro produkt a pracoviště
                const productIdentifiers = [pid, pcode, pname].filter(Boolean);
                const workplaceIdentifiers = [wid, wcode, wname].filter(Boolean);
                addToFallbackMap(productIdentifiers, workplaceIdentifiers, minutes);
                break;
            }
            case 'BY_PRODUCT_COLLECTION_AND_WORKPLACE': {
                const pcid = ct.productCollectionId as any;
                const pcname = (ct as any).productCollection?.name; // ProductCollection má jen name (ne code)
                const wid = ct.workplaceId as any;
                const wcode = (ct as any).workplace?.code;
                const wname = (ct as any).workplace?.name;
                
                // Prioritní mapa podle ID
                if (pcid && wid) {
                    const key = key2Strict(pcid, wid);
                    if (!maps.byProductCollectionWorkplace.has(key)) {
                        maps.byProductCollectionWorkplace.set(key, minutes);
                    }
                }
                
                // Fallback: všechny kombinace
                const pcIdentifiers = [pcid, pcname].filter(Boolean);
                const workplaceIdentifiers = [wid, wcode, wname].filter(Boolean);
                addToFallbackMap(pcIdentifiers, workplaceIdentifiers, minutes);
                break;
            }
            case 'BY_PRODUCT_AND_LOCATION': {
                const pid = ct.productId as any;
                const pcode = (ct as any).product?.code;
                const pname = (ct as any).product?.name;
                const lid = ct.locationId as any;
                const lcode = (ct as any).location?.code;
                const lname = (ct as any).location?.name;
                
                // Prioritní mapa podle ID
                if (pid && lid) {
                    const key = key2Strict(pid, lid);
                    if (!maps.byProductLocation.has(key)) {
                        maps.byProductLocation.set(key, minutes);
                    }
                }
                
                // Fallback: všechny kombinace
                const productIdentifiers = [pid, pcode, pname].filter(Boolean);
                const locationIdentifiers = [lid, lcode, lname].filter(Boolean);
                addToFallbackMap(productIdentifiers, locationIdentifiers, minutes);
                break;
            }
            case 'BY_PRODUCT_COLLECTION_AND_LOCATION': {
                const pcid = ct.productCollectionId as any;
                const pcname = (ct as any).productCollection?.name;
                const lid = ct.locationId as any;
                const lcode = (ct as any).location?.code;
                const lname = (ct as any).location?.name;
                
                // Prioritní mapa podle ID
                if (pcid && lid) {
                    const key = key2Strict(pcid, lid);
                    if (!maps.byProductCollectionLocation.has(key)) {
                        maps.byProductCollectionLocation.set(key, minutes);
                    }
                }
                
                // Fallback: všechny kombinace
                const pcIdentifiers = [pcid, pcname].filter(Boolean);
                const locationIdentifiers = [lid, lcode, lname].filter(Boolean);
                addToFallbackMap(pcIdentifiers, locationIdentifiers, minutes);
                break;
            }
            case 'BY_WORKPLACE': {
                const wid = ct.workplaceId as any;
                const wcode = (ct as any).workplace?.code;
                const wname = (ct as any).workplace?.name;
                
                // Prioritní mapa podle ID
                if (wid && !maps.byWorkplace.has(wid)) {
                    maps.byWorkplace.set(wid, minutes);
                }
                
                // Fallback: všechny identifikátory pracoviště (jako samostatný klíč)
                const workplaceIdentifiers = [wid, wcode, wname].filter(Boolean);
                for (const wIdentifier of workplaceIdentifiers) {
                    if (wIdentifier && !maps.fallbackMap.has(wIdentifier)) {
                        maps.fallbackMap.set(wIdentifier, minutes);
                    }
                }
                break;
            }
            default:
                break;
        }
    }


    // Resolver function with priority 1..5 and early exit
    // Pro každé pravidlo: nejprve zkusit ID, pak fallback na CODE/NAME (přímé O(1) lookupy)
    return (args: CycleTimeResolutionArgs): CycleTimeResolutionResult => {
        const wid = args.workplaceId ?? null;
        const wcode = args.workplaceCode ?? null;
        const wname = args.workplaceName ?? null;
        const pid = args.productId ?? null;
        const pcode = args.productCode ?? null;
        const pname = args.productName ?? null;
        const pcid = args.productCollectionId ?? null;
        const pcname = args.productCollectionName ?? null;
        const locChain = Array.isArray(args.locationChain) ? (args.locationChain as string[]) : [];
        const locChainCodes = Array.isArray(args.locationChainCodes) ? (args.locationChainCodes as string[]) : [];
        const locChainNames = Array.isArray(args.locationChainNames) ? (args.locationChainNames as string[]) : [];

        // Helper: Optimalizované O(1) lookupy místo vnořených cyklů
        // Zkusí konkrétní kombinace v pořadí: CODE+CODE, CODE+NAME, NAME+CODE, NAME+NAME
        function tryFallback(id1: string | null, code1: string | null, name1: string | null, 
                             id2: string | null, code2: string | null, name2: string | null): number | null {
            // Zkusíme nejčastější kombinace (po ID, které už bylo zkušeno)
            if (code1 && code2) { const v = maps.fallbackMap.get(key2(code1, code2)); if (typeof v === 'number') return v; }
            if (code1 && name2) { const v = maps.fallbackMap.get(key2(code1, name2)); if (typeof v === 'number') return v; }
            if (name1 && code2) { const v = maps.fallbackMap.get(key2(name1, code2)); if (typeof v === 'number') return v; }
            if (name1 && name2) { const v = maps.fallbackMap.get(key2(name1, name2)); if (typeof v === 'number') return v; }
            // Zkus i zbylé kombinace s ID (ID+CODE, ID+NAME, CODE+ID, NAME+ID)
            if (id1 && code2) { const v = maps.fallbackMap.get(key2(id1, code2)); if (typeof v === 'number') return v; }
            if (id1 && name2) { const v = maps.fallbackMap.get(key2(id1, name2)); if (typeof v === 'number') return v; }
            if (code1 && id2) { const v = maps.fallbackMap.get(key2(code1, id2)); if (typeof v === 'number') return v; }
            if (name1 && id2) { const v = maps.fallbackMap.get(key2(name1, id2)); if (typeof v === 'number') return v; }
            return null;
        }

        // 1) By product and workplace (ID prioritně, pak fallback)
        if (pid && wid) {
            const v = maps.byProductWorkplace.get(key2(pid, wid));
            if (typeof v === 'number') return { minutes: v, matchType: 1 };
        }
        const fallbackPW = tryFallback(pid, pcode, pname, wid, wcode, wname);
        if (fallbackPW !== null) return { minutes: fallbackPW, matchType: 1 };
        
        // 2) By product collection and workplace (ID prioritně, pak fallback)
        if (pcid && wid) {
            const v = maps.byProductCollectionWorkplace.get(key2(pcid, wid));
            if (typeof v === 'number') return { minutes: v, matchType: 2 };
        }
        const fallbackPC = tryFallback(pcid, null, pcname, wid, wcode, wname);
        if (fallbackPC !== null) return { minutes: fallbackPC, matchType: 2 };
        
        // 3) By product and location (ID prioritně, pak fallback pro každou lokaci v řetězci)
        for (const lid of locChain) {
            if (pid && lid) {
                const v = maps.byProductLocation.get(key2(pid, lid));
                if (typeof v === 'number') return { minutes: v, matchType: 3 };
            }
        }
        // Fallback: zkusíme kombinace s location codes a names
        for (let i = 0; i < Math.max(locChain.length, locChainCodes.length, locChainNames.length); i++) {
            const lid = locChain[i] ?? null;
            const lcode = locChainCodes[i] ?? null;
            const lname = locChainNames[i] ?? null;
            const fallbackPL = tryFallback(pid, pcode, pname, lid, lcode, lname);
            if (fallbackPL !== null) return { minutes: fallbackPL, matchType: 3 };
        }
        
        // 4) By product collection and location (ID prioritně, pak fallback)
        for (const lid of locChain) {
            if (pcid && lid) {
                const v = maps.byProductCollectionLocation.get(key2(pcid, lid));
                if (typeof v === 'number') return { minutes: v, matchType: 4 };
            }
        }
        // Fallback
        for (let i = 0; i < Math.max(locChain.length, locChainCodes.length, locChainNames.length); i++) {
            const lid = locChain[i] ?? null;
            const lcode = locChainCodes[i] ?? null;
            const lname = locChainNames[i] ?? null;
            const fallbackPCL = tryFallback(pcid, null, pcname, lid, lcode, lname);
            if (fallbackPCL !== null) return { minutes: fallbackPCL, matchType: 4 };
        }
        
        // 5) By workplace (fallback) - zkusíme ID/CODE/NAME jako samostatné klíče
        if (wid) {
            const v = maps.byWorkplace.get(wid);
            if (typeof v === 'number') return { minutes: v, matchType: 5 };
        }
        if (wcode) {
            const v = maps.fallbackMap.get(wcode);
            if (typeof v === 'number') return { minutes: v, matchType: 5 };
        }
        if (wname) {
            const v = maps.fallbackMap.get(wname);
            if (typeof v === 'number') return { minutes: v, matchType: 5 };
        }
        
        return { minutes: null, matchType: null };
    };
}
